import { observer } from 'src/utils/mobx-react';
import React from 'react';
import { AppState, useAppStateContext } from 'src/AppState/AppState';
import JsonView from '@microlink/react-json-view';
import { EventModelType } from 'src_common/common/websocket2/modelsApi/Event';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { SelectionModelType } from 'src_common/common/websocket2/modelsApi/Market';
import { computed, makeAutoObservable } from 'mobx';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

type DebugDataType = EventModelType & {
    cashoutValidation: {
        markets: FullMarketDebugType;
    };
};

type FullMarketDebugType = Record<
    string,
    { market: EventMarketItemType; selections: Record<string, SelectionModelType> }
>;

class EventDebugState {
    public constructor(
        private readonly appState: AppState,
        private readonly eventId: () => EventId | null
    ) {
        makeAutoObservable(this);
    }

    @computed public get getDebugData(): DebugDataType | null {
        const eventId = this.eventId();
        if (eventId === null) {
            console.error('get debug data, eventId is null');
            return null;
        }

        const eventModel = this.appState.sdkStaff.models.getEvent(eventId.toOldId());

        if (eventModel === null) {
            console.error('get debug data, eventModel is null');
            return null;
        }

        const debugInfo: DebugDataType = {
            ...eventModel.getData(),
            cashoutValidation: {
                markets: {},
            },
        };

        debugInfo.cashoutValidation.markets = this.combineMarketsAndSelection ?? {};

        return debugInfo;
    }

    @computed public get combineMarketsAndSelection(): FullMarketDebugType | null {
        const eventId = this.eventId();
        if (eventId === null) {
            return null;
        }

        const eventMarkets = this.appState.sdkStaff.models.getEventMarketsAll(eventId.toOldId());
        if (eventMarkets === null) {
            return null;
        }

        let debugMarket: Record<string, EventMarketItemType> = {};
        for (const market of eventMarkets) {
            debugMarket = {
                ...debugMarket,
                [market.name]: market,
            };
        }

        let fullMarketData: FullMarketDebugType = {};
        for (const [marketName, market] of Object.entries(debugMarket)) {
            const fullMarket = this.appState.sdkStaff.models.getMarket(market.id);
            if (fullMarket === null) {
                continue;
            }

            for (const selection of fullMarket.selections) {
                const currentMarketData = fullMarketData[marketName];
                let selections = {};
                if (currentMarketData === undefined) {
                    selections = { [selection.name]: selection.getData() };
                } else {
                    selections = {
                        ...currentMarketData.selections,
                        [selection.name]: selection.getData(),
                    };
                }
                fullMarketData = {
                    [marketName]: {
                        market,
                        selections,
                    },
                };
            }
        }
        return fullMarketData;
    }

    public publishEventSnapshot = async (): Promise<void> => {
        const eventId = this.eventId();
        if (eventId === null) {
            console.error('publish snapshot, eventId is null');
            return;
        }

        await this.appState.sdkStaff.trading.publishEventSnapshot(eventId);
    };
}

type EventDebugPropsType = {
    eventId: EventId;
};

export const EventDebug = observer('EventDebug', (props: EventDebugPropsType) => {
    const appState = useAppStateContext();
    const state = new EventDebugState(appState, () => props.eventId);

    return (
        <>
            <h3>Extra actions</h3>
            <Button onClick={state.publishEventSnapshot}>Publish snapshot</Button>
            <h3>Debug data</h3>
            <JsonView
                src={state.getDebugData ?? {}}
                displayDataTypes={false}
                collapsed={2}
                name={null}
                groupArraysAfterLength={1000}
                style={{ fontSize: '14px' }}
            />
        </>
    );
});
